// All imports are now lazy-loaded via dynamic import() as needed

import axios, { AxiosInstance } from "axios";
import { ChatPosition } from "./v2/core/entities/ChatSettingsV2";
import { API_URL } from "./constants";
import { SendClientEventOptions } from "./types";

let S3_CONFIG: any;
let ChatSettings: any, ISDKSettings: any, quackChatConfigSchema: any;
let getBackupValuesFromLS: any;
let renderChat: any;
let MixpanelEvents: any, mixTrackEvent: any;

async function loadConstants() {
  if (!S3_CONFIG) {
    const mod = await import("./constants");
    S3_CONFIG = mod.S3_CONFIG;
  }
}

async function loadTypes() {
  if (!ChatSettings || !ISDKSettings || !quackChatConfigSchema) {
    const mod: any = await import("./types");
    ChatSettings = mod.ChatSettings;
    ISDKSettings = mod.ISDKSettings;
    quackChatConfigSchema = mod.quackChatConfigSchema;
  }
}

async function loadLib() {
  if (!getBackupValuesFromLS) {
    const mod = await import("./lib");
    getBackupValuesFromLS = mod.getBackupValuesFromLS;
  }
}

async function loadChat() {
  if (!renderChat) {
    const mod = await import("./chat");
    renderChat = mod.renderChat;
  }
}

async function loadMixpanel() {
  if (!MixpanelEvents || !mixTrackEvent) {
    const mod = await import("./assets/mixpanel");
    MixpanelEvents = mod.MixpanelEvents;
    mixTrackEvent = mod.mixTrackEvent;
  }
}

let defaultSDKSettings: any;
let defaultChatSettings: any;

async function getDefaultSDKSettings() {
  await loadTypes();
  if (!defaultSDKSettings) {
    defaultSDKSettings = {
      tenantId: "",
      version: "v1",
      isActive: true,
      isTokenMasked: false,
      chat: false,
      openChatDefaultList: [""],
      whiteListUrls: [""],
      proactiveListUrls: [""],
      proactiveTime: 0,
      inject: false,
    };
  }
  return defaultSDKSettings;
}

async function getDefaultChatSettings() {
  await loadTypes();
  if (!defaultChatSettings) {
    defaultChatSettings = {
      name: "Quack demo's AI Agent",
      quackAgentName: "AI Assistant",
      customSVG:
        '<svg fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" shape-rendering="geometricPrecision" viewBox="0 0 24 24" class="jsx-3060654314" height="24" width="24" style="color: currentcolor;"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"></path></svg>',
      borderRadius: 8,
      iconSize: 48,
      iconPadding: 4,
      poweredByLine: false,
      escalationMessageToLiveAgent:
        "We understand that this inquiry requires the help of one of our Support Agents. Please hold while we connect you to one of our Agents.",
      escalationMessageToNotLiveAgent:
        "We understand that this inquiry requires the help of one of our Support Agents. At this time all of our agents are offline, so we will open a ticket to the team on your behalf. They will get back to you as soon as they can.",
      theme: {
        iconPrimary: "#1A1A1A",
        widgetPrimary: "#1A1A1A",
        background: "#FFFFFF",
        lightBorder: "#656665",
        textPlaceholder: "#8F8F8F",
        borderDefault: "#F1F1F1",
        bgCard: "#E6E6E6",
        bgInverted: "#1D1D1D",
        textPrimary: "#1A1A1A",
        textInverted: "#FFFFFF",
        borderLight: "#CBCBCB",
        bgPrimaryQuack: "#FFFFFF",
        bgChatWidget: "#ffffff00",
        bgInput: "#F0F0F0",
        agentBackground: "#6F5BE2",
        agentText: "#FAFBFC",
        userBackground: "#FFFFFF",
        userText: "#000000",
        bgSendButton: "#202020",
        textSendButton: "#fcfcfc",
      },
      disappearAfterClose: 10,
      fontSize: 14,
      waitTimeBeforeRefreshAfterEscalating: 300,
      openFullyOnOpenChat: false,
      csatDisabled: false,
      csatType: "rating",
      allowCsatComment: false,
      csatCommentText: "Help us improve your experience",
      allowUploadFileBeforeEscalation: false,
      placeholder: "Ask anything...",
      enablePreChatFlow: false,
      preChatWelcomeMessage: "Welcome 👋",
      preChatWelcomeSubtitle: "Let's get started! Please fill in your details below.",
      preChatCaseTitle: "How can we help you today?",
      preChatAvatarRadius: "regular",
      layout: {
        greeting: {
          enabled: true,
          title: "Hello {USER}",
          fallbackUserName: "there",
        },
        askAiInput: {
          enabled: true,
          placeholder: "Ask anything...",
        },
        supportTickets: {
          enabled: true,
          statusesToDisplayOnThePage: [],
          endUserTicketName: "Support Cases",
          allowUserReplyToTicket: false,
          style: "list",
        },
      },
    };
  }
  return defaultChatSettings;
}

function uuidv4() {
  // @ts-ignore
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
  );
}

async function getComponent() {
  const { QuackSDK } = await import("./index");
  const q = new QuackSDK();
  return q;
}

type LoadOptions = {
  onReady?: (quack: Quack) => void;
};

// unbind the triggering of end function from the start

class Quack {
  token: string;
  email: string;
  anonId: string;
  correlationId: string;
  QuackSdk: any;
  settings: any;
  chatSettings: any;
  chatStarted: boolean;
  chatSessionId?: string;
  identifyBackup: any;
  journeyEvents: any[];
  clickTrackingStarted: boolean;
  private clickTrackingUnsubscribe: (() => void) | null;
  private visibilityRefreshListenerAttached: boolean;
  api: AxiosInstance | null;
  trackFunc: (eventName: string, properties: any) => void;
  errorRepotingCallback: (error: unknown) => void;
  authCallback: () => {
    token: string;
    type: string;
    debug: boolean;
  };

  constructor() {
    this.correlationId = "";
    this.anonId = "";
    this.token = "";
    this.email = "";
    this.QuackSdk = undefined;
    this.settings = {};
    this.chatSettings = {};
    this.chatStarted = false;
    this.api = null;
    this.journeyEvents = [];
    this.clickTrackingStarted = false;
    this.clickTrackingUnsubscribe = null;
    this.visibilityRefreshListenerAttached = false;
    this.trackFunc = () => {};
    this.track = this.track.bind(this);
    this.refresh = this.refresh.bind(this);
    this.showChat = this.showChat.bind(this);
    this.hideChat = this.hideChat.bind(this);
    this.trackCallback = this.trackCallback.bind(this);
    this.disableProactive = this.disableProactive.bind(this);
    this.enableProactive = this.enableProactive.bind(this);
    this.identify = this.identify.bind(this);
    this.getIdentify = this.getIdentify.bind(this);
    this.getJourney = this.getJourney.bind(this);
    this.logout = this.logout.bind(this);
    this.addErrorHandler = this.addErrorHandler.bind(this);
    this.addAuthHandler = this.addAuthHandler.bind(this);
    this.triggerAuthHandler = this.triggerAuthHandler.bind(this);
    this.minimizeChat = this.minimizeChat.bind(this);
    this.expandChat = this.expandChat.bind(this);
    this.alignChat = this.alignChat.bind(this);
    this.reload = this.reload.bind(this);

    // initialize settings and chatSettings asynchronously
    getDefaultSDKSettings().then((settings) => {
      this.settings = settings;
    });
    getDefaultChatSettings().then((chatSettings) => {
      this.chatSettings = chatSettings;
    });

    this.startClickTracking();
  }

  logger = async (message: string, { level = "info", extraData }: SendClientEventOptions) => {
    if (this.api === null) {
      return;
    }

    return this.api.post<void>("/sessions-api/quackchat/event", {
      token: this.token,
      level,
      message,
      extraData,
    });
  };

  initApi() {
    this.api = axios.create({
      baseURL: API_URL,
      timeout: 45_000,
      headers: {
        "Content-Type": "application/json",
        ...(this.token != null && { "x-token-id": this.token }),
      },
    });
  }

  async loadQuackSdk() {
    try {
      this.QuackSdk = await getComponent();

      void this.logger("quack sdk loaded successfully, initializing quack sdk", {
        extraData: {
          token: this.token,
        },
      });

      await this.QuackSdk.initiate({
        correlationId: this.correlationId,
        organizationId: this.settings.tenantId,
        settings: this.settings,
        token: this.token,
        anonId: this.anonId,
        identifyBackup: this.identifyBackup,
      });

      if (this.identifyBackup) {
        void this.logger("identify backup was provided, identifying user", {
          extraData: {
            identifyBackup: this.identifyBackup,
          },
        });

        await this.QuackSdk.identify(this.identifyBackup);
        this.identifyBackup = undefined;
      }
    } catch (error) {
      void this.logger("failed to load quack sdk", {
        level: "error",
        extraData: {
          error: error instanceof Error ? error.message : "failed to load quack sdk",
        },
      });
    }
  }

  logout() {
    setTimeout(() => {
      this.QuackSdk?.logout();
      this.email = "";
    }, 2000);
  }

  /**
   * Re-fetches remote configuration and merges into `settings` / `chatSettings`.
   * Used on init and when refreshing (tab focus, chat reopen).
   */
  private async reloadRemoteConfiguration(): Promise<boolean> {
    await loadConstants();
    await loadTypes();
    await loadLib();

    try {
      const result = await fetch(`${S3_CONFIG}configurations/${this.token}.json`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      });

      const response = await result.json();

      const { error, data } = quackChatConfigSchema.safeParse(response);

      if (error) {
        console.log("error", error);
        void this.logger("invalid configuration", {
          level: "error",
          extraData: {
            error: error instanceof Error ? error.message : "failed to load quack sdk",
          },
        });
        throw new Error("Quack: Invalid configuration");
      }

      const { chatSettings, ...settings } = data;
      if (settings) {
        this.settings = { ...this.settings, ...settings };
      }

      if (process.env.NODE_ENV === "development") {
        const { openChatDefaultList, whiteListUrls } = this.settings;
        this.settings.openChatDefaultList = [...openChatDefaultList, "file"];
        this.settings.whiteListUrls = [...whiteListUrls, "file"];
      }

      if (chatSettings) {
        this.chatSettings = {
          ...this.chatSettings,
          ...response.chatSettings,
          theme: {
            ...this.chatSettings.theme,
            ...response.chatSettings.theme,
          },
        };
      }

      if (this.chatSettings?.trackWithoutReporting) {
        import("./requestTracking").then(({ startRequestTracking }) => {
          startRequestTracking();
        });
      }

      return true;
    } catch (e) {
      console.error("Quack: seems like your quack token is wrong");
      return false;
    }
  }

  /**
   * Refetch remote config into window.quack (settings / chatSettings).
   */
  async refresh(reason: string): Promise<void> {
    // console.log(`[Quack] refresh (${reason}) — refetching window.quack configuration`);
    if (!this.token) {
      console.log("[Quack] refresh skipped: no token");
      return;
    }
    const ok = await this.reloadRemoteConfiguration();
    // if (ok) {
    //   console.log(`[Quack] refresh done (${reason}) — window.quack updated from remote`);
    // } else {
    //   console.log(`[Quack] refresh failed (${reason})`);
    // }
  }

  private attachVisibilityRefreshListener(): void {
    if (this.visibilityRefreshListenerAttached || typeof document === "undefined") {
      return;
    }
    this.visibilityRefreshListenerAttached = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible" || !this.token) {
        return;
      }
      // console.log(
      //   "[Quack] scenario: user returned to this tab from another tab — refreshing window.quack",
      // );
      void this.refresh("tab_visibility_return");
    });
  }

  // initiate with token
  async initiate(token: string, logMessage = "initiate with token") {
    void this.logger(logMessage, {
      extraData: {
        token,
      },
    });

    this.token = token;

    this.initApi();

    try {
      const ok = await this.reloadRemoteConfiguration();
      if (!ok) {
        return;
      }

      const backupValues = getBackupValuesFromLS();

      this.anonId = backupValues.anonId ?? uuidv4();
      this.correlationId = backupValues.correlationId ?? uuidv4();

      if (this.settings.inject) {
        // instead should load here other script
        await this.loadQuackSdk();

        setTimeout(() => {
          if (window?.HelpCenter) {
            this.identify({
              email: window?.HelpCenter?.user?.email,
              id: window?.HelpCenter?.user?.identifier,
              ...(window?.HelpCenter.user ?? {}),
            });
          }
        }, 1000);
      }

      if (this.settings.chat) {
        this.setupUrlChangeListener();
      }

      this.attachVisibilityRefreshListener();
    } catch (e) {
      console.error("Quack: seems like your quack token is wrong");
    }

    // HARDCODED FOR CHILIPIPER
    if (this.settings.tenantId === "chilipiper") {
      const searchParams = new URLSearchParams(window.location.search);
      const searchJson: any = {};
      for (const [key, value] of searchParams.entries()) {
        searchJson[key] = value;
      }

      setTimeout(() => {
        if (searchJson.type === "MM" || searchJson.type === "Onboarding") {
          this.expandChat();
        }
      }, 800);
    }
  }

  load(token: string, { onReady }: LoadOptions = {}) {
    this.initiate(token, "initiate with token from load fn").then(() => {
      onReady?.(this);
    });
  }

  reload(token: string, { onReady }: LoadOptions = {}) {
    this.hideChat();
    this.QuackSdk?.logout();
    this.email = "";
    this.chatStarted = false;
    this.identifyBackup = undefined;
    this.chatSessionId = undefined;
    localStorage.removeItem("quack-backup");
    this.load(token, { onReady });
  }

  startChatSession(chatSessionId: string) {
    this.QuackSdk?.startChatSession(chatSessionId);
    this.chatSessionId = chatSessionId;
  }

  async proactiveTrigger(message: string) {
    this.QuackSdk?.proactiveTrigger(message);
  }

  async authenticate(accessToken: string) {
    await this.QuackSdk?.authenticate(accessToken);
  }

  private async identifyUser(props: any) {
    try {
      const email = this.QuackSdk.identify(props);
      if (email) {
        this.email = email;
      }
    } catch (error) {
      // void this.logger("failed to identify user", {
      //   level: "error",
      //   extraData: {
      //     error: error instanceof Error ? error.message : "failed to identify user",
      //   },
      // });
    }
  }

  async identify(props: any) {
    // Optimistically identify the user - for new implementation
    await this.identifyUser(props);

    // save the identify backup to be used later
    this.identifyBackup = props;
  }

  getIdentify() {
    return this.QuackSdk?.getIdentify();
  }

  getJourney(): any[] {
    const sdkEvents = Array.isArray(this.QuackSdk?.events) ? this.QuackSdk.events : [];
    const combined = [...sdkEvents, ...this.journeyEvents];
    return combined.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
  }

  private startClickTracking(): void {
    if (typeof document === "undefined" || this.clickTrackingStarted) {
      return;
    }
    this.clickTrackingStarted = true;

    const getSelector = (el: EventTarget | null): string => {
      if (!el || !(el instanceof Element)) return "";
      const e = el as Element;
      if (e.id) return `#${e.id}`;
      const path: string[] = [];
      let current: Element | null = e;
      let depth = 0;
      while (current && depth < 5) {
        let sel = current.tagName.toLowerCase();
        if (current.className && typeof current.className === "string") {
          const cls = current.className.trim().split(/\s+/)[0];
          if (cls) sel += "." + cls.replace(/\s/g, ".");
        }
        path.unshift(sel);
        current = current.parentElement;
        depth++;
      }
      return path.join(" > ");
    };

    const handleClick = (e: MouseEvent): void => {
      const target = e.target;
      this.journeyEvents.push({
        type: "click",
        timestamp: Date.now(),
        url: typeof window !== "undefined" ? window.location.href : "",
        tagName:
          target && (target as Element).tagName ? (target as Element).tagName.toLowerCase() : "",
        selector: getSelector(target),
        clientX: e.clientX,
        clientY: e.clientY,
      });
    };

    document.addEventListener("click", handleClick, true);
    this.clickTrackingUnsubscribe = () => {
      document.removeEventListener("click", handleClick, true);
      this.clickTrackingUnsubscribe = null;
    };
  }

  async clear() {
    this.journeyEvents = [];
    this.QuackSdk?.clear();
  }

  setCustom(name: string, func: () => void) {
    // @ts-ignore
    this.QuackSdk[name] = func;
  }

  async track(eventName: string, properties: any) {
    await loadMixpanel();
    mixTrackEvent({
      tenant: this.settings.tenantId,
      sessionId: this.correlationId,
      properties,
      event: eventName as typeof MixpanelEvents,
    });
    this.trackFunc(eventName, properties);
  }

  async showChat() {
    await loadChat();

    if (!this.QuackSdk) {
      await this.loadQuackSdk();
    }

    const quackContainer = document.getElementById("quack-container");

    if (quackContainer != null && quackContainer.children.length > 0) {
      return;
    }

    if (quackContainer == null) {
      const el = document.createElement("div");
      el.id = "quack-container";
      document.body.appendChild(el);
    }

    renderChat({
      ...this.settings,
      chatSettings: this.chatSettings,
      token: this.token,
      sessionId: this.correlationId,
      email: this.email,
      track: (eventName: string, properties: any) => this.track(eventName, properties),
      contextAwareQuestionsConfiguration: this.settings.contextAwareQuestionsConfiguration,
      contextArrowsHelperConfiguration: this.settings.contextArrowsHelperConfiguration,
    });

    this.chatStarted = true;
  }
  minimizeChat() {
    window.postMessage({ type: "QUACK_CLOSE_CHAT" }, "*");
  }

  expandChat() {
    window.postMessage({ type: "QUACK_OPEN_CHAT" }, "*");
  }

  alignChat(position: ChatPosition) {
    window.postMessage({ type: "QUACK_ALIGN_CHAT", position }, "*");
  }

  disableProactive() {
    window.postMessage("quack-disable-proactive", "*");
  }

  enableProactive() {
    window.postMessage("quack-enable-proactive", "*");
  }

  hideChat() {
    const quackContainer = document.getElementById("quack-container");
    if (quackContainer) {
      quackContainer.remove();
    }
  }

  trackCallback(callback: (eventName: string, properties: any) => void) {
    this.trackFunc = callback;
  }

  setupUrlChangeListener() {
    const checkUrlAndShowChat = () => {
      const currentUrl = window.location.href;
      const openChatDefault = this.settings.openChatDefaultList.some((url: string) =>
        currentUrl.includes(url),
      );
      if (this.settings.chat && !this.chatStarted && openChatDefault) {
        setTimeout(() => this.showChat(), 300);
      }
    };

    checkUrlAndShowChat();

    (function (history) {
      const pushState = history.pushState;
      const replaceState = history.replaceState;

      history.pushState = function (state, title, url) {
        const result = pushState.apply(history, arguments);
        checkUrlAndShowChat();
        return result;
      };

      history.replaceState = function (state, title, url) {
        const result = replaceState.apply(history, arguments);
        checkUrlAndShowChat();
        return result;
      };

      window.addEventListener("popstate", checkUrlAndShowChat);
    })(window.history);
  }

  addErrorHandler(callback: (error: unknown) => void) {
    this.errorRepotingCallback = callback;
  }

  addAuthHandler(
    callback: () => {
      token: string;
      type: string;
      debug: boolean;
    },
  ) {
    this.authCallback = callback;
  }

  triggerAuthHandler() {
    const { token, type, debug } = this.authCallback();
    if (debug) {
      console.log(`Quack auth handler triggered`, {
        token,
        type,
      });
    }
  }
}

export default Quack;
